import { useState, useMemo, useRef, useCallback } from 'react';
import { ScrollSyncPane } from 'react-scroll-sync';
import Decimal from 'decimal.js';

import { StyledSummaryEditElement } from '../../../atoms/SummaryEditElement/StyledSummaryEditElement';
import { StyledSavePayElement } from '../../../atoms/PayElement/StyledSavePayElement';
import { StyledClosePayElement } from '../../../../styles/sharedStyles';

import CloseIcon from '../../../atoms/UserPanelStudens/CloseIcon';
import PencilIcon from '../../../atoms/UserPanelStudens/PencilIcon';
import AcceptIcon from '../../../atoms/UserPanelStudens/AcceptIcon';
import { getLang, getString } from '../../../../strings';
import SummaryEditElementEditableTable from './SummaryEditElementEditableTable';
import SummaryEditElementTable from './SummaryEditElementTable';
import { handleDelete, handleSubmit } from './SummaryEditElement.data';
import DeleteHourConfirmationPopup from './DeleteHourConfirmationPopup';
import MarkHourAsPaidConfirmPopup from '../../MarkHourAsPaidConfirmPopup/MarkHourAsPaidConfirmPopup';
import { markHourAsPaid } from '../../../../logic/requests/students';
import { getDateString } from '../../../../logic/eventtime';

const SummaryEditElement = ({
    hour,
    setHour,
    deleteHour,
    students,
    trainers,
    subjects,
    isAdmin,
}) => {
    const [isEdit, setIsEdit] = useState(false);
    const [confirmPopup, setConfirmPopup] = useState(false);
    const [markAsPaidConfirmPopup, setMarkAsPaidConfirmPopup] = useState(false);
    const [reason, setReason] = useState(false);
    const [textareaError, setTextareaError] = useState(null);

    const student = useMemo(() => `${hour.student.firstname} ${hour.student.lastname}`, [hour]);
    const trainer = useMemo(() => `${hour.trainer.firstname} ${hour.trainer.lastname}`, [hour]);

    const defaultValues = useMemo(
        () => ({
            student: hour.student.id,
            date: new Date(hour.date).toISOString().split('T')[0],
            hours: hour.half ? '0.5' : hour.hours,
            subject: hour.subject,
            trainer: hour.trainer.id,
            price: hour.price,
            price_trainer: hour.price_trainer,
        }),
        [hour]
    );

    const price = useMemo(() => new Decimal(hour.price), [hour]);
    const price_trainer = useMemo(() => new Decimal(hour.price_trainer), [hour]);

    const values = {
        student: useRef(),
        date: useRef(),
        hours: useRef(),
        subject: useRef(),
        trainer: useRef(),
        price: useRef(),
        price_trainer: useRef(),
    };

    const handleAcceptHour = useCallback(() => {
        markHourAsPaid(hour.id).then((res) => {
            setMarkAsPaidConfirmPopup(false);
            setHour((hour) => ({
                ...hour,
                paid: 1,
            }));
        });
    }, [hour.id, setHour]);

    return (
        <>
            <DeleteHourConfirmationPopup
                confirmPopup={confirmPopup}
                setConfirmPopup={setConfirmPopup}
                isAdmin={isAdmin}
                reason={reason}
                setReason={setReason}
                textareaError={textareaError}
                setTextareaError={setTextareaError}
                hour={hour}
                handleDelete={() =>
                    handleDelete(
                        hour.id,
                        isAdmin,
                        deleteHour,
                        confirmPopup,
                        setConfirmPopup,
                        reason,
                        setTextareaError
                    )
                }
                getString={getString}
            />
            <MarkHourAsPaidConfirmPopup
                confirmPopup={markAsPaidConfirmPopup}
                setConfirmPopup={setMarkAsPaidConfirmPopup}
                handleAcceptHour={handleAcceptHour}
                student={student}
                date={getDateString(new Date(hour.date))}
                hours_count={hour.half ? '0.5' : hour.hours}
                subject={
                    subjects?.find((el) => el.description && el.id === hour.subject)?.description[
                        getLang()
                    ]
                }
                getString={getString}
            />
            <StyledSummaryEditElement>
                <ScrollSyncPane>
                    <div>
                        {!isEdit ? (
                            <SummaryEditElementTable
                                isAdmin={isAdmin}
                                hour={hour}
                                student={student}
                                trainer={trainer}
                                price={price}
                                price_trainer={price_trainer}
                                subjects={subjects}
                            />
                        ) : (
                            <SummaryEditElementEditableTable
                                isAdmin={isAdmin}
                                students={students}
                                trainers={trainers}
                                subjects={subjects}
                                defaultValues={defaultValues}
                                values={values}
                            />
                        )}
                    </div>
                </ScrollSyncPane>
                <StyledSavePayElement
                    style={{ transform: 'translateY(0)', minWidth: '40px', minHeight: '40px' }}
                    $isDisabled={hour.paid === 1}
                    disabled={hour.paid === 1}
                    onClick={() => setMarkAsPaidConfirmPopup((prev) => !prev)}
                    type="submit"
                    isedit
                >
                    <AcceptIcon />
                </StyledSavePayElement>
                <StyledSavePayElement
                    isedit={isEdit}
                    onClick={
                        isEdit
                            ? () =>
                                  handleSubmit(
                                      hour.id,
                                      Object.fromEntries(
                                          Object.entries(values).map(([key, val]) => [
                                              key,
                                              ['student', 'trainer'].includes(key)
                                                  ? parseInt(val?.current?.value)
                                                  : val?.current?.value,
                                          ])
                                      ),
                                      defaultValues,
                                      (newHour) => {
                                          setIsEdit(false);
                                          setHour((hour) => ({
                                              ...hour,
                                              ...newHour,
                                              student: newHour?.student
                                                  ? students.find((s) => s.id === newHour.student)
                                                  : hour.student,
                                              trainer: newHour?.trainer
                                                  ? trainers.find((t) => t.id === newHour.trainer)
                                                  : hour.trainer,
                                          }));
                                      }
                                  )
                            : () => setIsEdit(true)
                    }
                >
                    {!isEdit ? <PencilIcon /> : <AcceptIcon />}
                </StyledSavePayElement>
                <StyledClosePayElement
                    disabled={hour.paid === 1 && hour.free !== 1}
                    onClick={() =>
                        handleDelete(
                            hour.id,
                            isAdmin,
                            deleteHour,
                            confirmPopup,
                            setConfirmPopup,
                            reason,
                            setTextareaError
                        )
                    }
                >
                    <CloseIcon />
                </StyledClosePayElement>
            </StyledSummaryEditElement>
        </>
    );
};

export default SummaryEditElement;
